import { Controller } from "@hotwired/stimulus"

// Reloads a list's Turbo frame when the server says the list changed (shared/_list_refresh_signal).
//
// A configurable table renders each row in the viewer's own column layout, so the server cannot
// broadcast one rendered row to everyone the way the old lists did. It broadcasts this marker
// instead — replacing it with a copy whose `signal` value is true — and every open list re-fetches
// its own frame. The marker on the initial page carries `signal: false` and does nothing.
//
// The reload waits while the user is in the middle of something the re-render would throw away:
// an open drawer (#offcanvas), ticked bulk checkboxes, or an open dialog or dropdown menu inside the
// frame — the morph would close those, because the server renders them closed. It is debounced,
// because a bulk action commits one record at a time and each commit sends a signal.
export default class extends Controller {
  static values = {
    frame: String,
    signal: Boolean,
    debounce: { type: Number, default: 400 },
    retry: { type: Number, default: 500 },
  }

  connect() {
    if (this.signalValue) this.schedule(this.debounceValue)
  }

  disconnect() {
    clearTimeout(this.timeout)
  }

  schedule(delay) {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.reload(), delay)
  }

  reload() {
    const frame = document.getElementById(this.frameValue)
    if (!frame) return

    if (this.busy(frame)) {
      this.schedule(this.retryValue)
      return
    }

    // A frame that was never navigated has no src; its content came with the page. Turbo only
    // morphs (refresh="morph") inside reload(), and only when src is already set — assigning src
    // alone would replace the frame, losing the table's scroll position. Setting src starts a
    // plain load that reload() cancels straight away.
    if (!frame.src) frame.src = window.location.href
    frame.reload()
  }

  busy(frame) {
    return (
      document.getElementById("offcanvas") !== null ||
      frame.querySelector('input[name="ids[]"]:checked') !== null ||
      frame.querySelector('dialog[open], [data-dropdown-target="menu"]:not(.hidden)') !== null
    )
  }
}
